import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';

import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

import { AccountLicenseService } from 'rev-shared/security/AccountLicense.Service';
import { CategoryService } from 'rev-shared/media/Category.Service';
import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { ICategorySettings } from 'rev-shared/ui/categorySettings/ICategorySettings';
import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';
import { IUnsubscribe } from 'rev-shared/push/IUnsubscribe';
import { RecordingPolicy } from 'rev-shared/media/RecordingPolicy';
import { TemplateDialogName } from 'rev-shared/ui/templates/TemplateDialogAngular.Component';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { VbConfirmationDialogComponent } from 'rev-shared/ui/dialog/VbConfirmationDialogAngular.Component';
import { WebcastTemplatesService } from 'rev-shared/webcast/WebcastTemplates.Service';
import { noop } from 'rev-shared/util';

import { EventAccessControl } from 'rev-portal/scheduledEvents/EventAccessControl';
import { PresentationFileStatus } from 'rev-portal/scheduledEvents/presentations/PresentationFileStatus';
import { ThemeService } from 'rev-portal/branding/Theme.Service';
import { WebcastPreviewService } from 'rev-portal/scheduledEvents/webcast/WebcastPreview.Service';
import { WebcastVideoSource } from 'rev-portal/scheduledEvents/webcast/WebcastVideoSource';

import { EditWebcastFormComponent } from './EditWebcastForm.Component';
import { EditWebcastModel } from './EditWebcastModel';
import { EditWebcastService } from './EditWebcast.Service';

import styles from './EditWebcast.module.less';
import './event-form.less';

@Component({
	selector: 'edit-webcast',
	templateUrl: './EditWebcast.Component.html',
	standalone: false
})
export class EditWebcastComponent implements OnChanges, OnDestroy, OnInit {
	@Input() public webcastId: string;
	@Input() public editWebcast: EditWebcastModel;
	@Input() public defaultStartDate: Date;
	@Input() public cloneWebcastId: string;
	@Input() public isSidebar: boolean;
	@Input() public mediaFeatures: IMediaFeatures;

	@Output() public onSaved: EventEmitter<any> = new EventEmitter();
	@Output() public onCancelled: EventEmitter<any> = new EventEmitter();
	@Output() public sourceRestartInitiated: EventEmitter<string> = new EventEmitter();
	@Output() public switchStreamInitiated: EventEmitter<void> = new EventEmitter();

	public readonly styles = styles;
	public webcast: EditWebcastModel;
	public status: { readonly [key: string]: boolean };
	public ready: boolean;

	public categories: ICategorySettings[];

	@ViewChild('createSuccessDialog')
	public createSuccessDialog: VbConfirmationDialogComponent;
	@ViewChild('saveSuccessDialog')
	public saveSuccessDialog: VbConfirmationDialogComponent;
	@ViewChild(EditWebcastFormComponent)
	public editWebcastForm: EditWebcastFormComponent;

	private pushSubscription: IUnsubscribe;
	private assignableCategoryIds: string[];

	constructor(
		private readonly element: ElementRef,
		private readonly accountLicense: AccountLicenseService,
		private readonly CategoryService: CategoryService,
		private readonly Dialog: DialogService,
		private readonly EditWebcastService: EditWebcastService,
		private readonly ThemeService: ThemeService,
		private readonly Translate: TranslateService,
		private readonly UserContext: UserContextService,
		private readonly WebcastPreviewService: WebcastPreviewService,
		private readonly WebcastTemplatesService: WebcastTemplatesService
	){
	}

	public ngOnInit(): void {
		this.status = { processing: true };

		Promise.all([
			this.initWebastModel(),
			this.accountLicense.updateLicenseHoursValid(),
			this.loadFlattenedCategories(),
		])
			.then(() => {
				this.ready = true;
				this.status = { active: true };

				this.assignableCategoryIds = (this.categories || [])
					.filter(x => x.isAssignable)
					.map(x => x.categoryId);

				//from template or duplicate
				if (this.webcast.categoryIds.length && !this.webcast.id) {
					this.webcast.setAssignableCategories(this.assignableCategoryIds);
				}
			})
			.catch((e: any) => {
				this.status = { error: true };
				console.error(e);
			});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if(changes.isSidebar) {
			this.element.nativeElement.classList.toggle(styles.sidebarLayout, this.isSidebar);
		}
	}

	public ngOnDestroy(): void {
		if (this.pushSubscription) {
			this.pushSubscription();
		}
	}

	private initWebastModel(): Promise<any> {
		const editWebcastModel = this.WebcastPreviewService.fromPreviewButton
			? this.WebcastPreviewService.webcastForm
			: this.editWebcast;

		return Promise.resolve(editWebcastModel || this.loadWebcast())
			.then((webcast: EditWebcastModel) => {
				const presentationStatus = webcast.presentation?.status;
				if (presentationStatus && presentationStatus !== PresentationFileStatus.Complete) {
					this.pushSubscription = webcast.presentation.registerPushHandlers();
				}
				this.webcast = webcast;
				this.WebcastPreviewService.fromPreviewButton = false;
			});
	}

	private loadWebcast(): Promise<any> {
		const recordingDisabled = this.mediaFeatures.recordingPolicy === RecordingPolicy.DISABLE;

		const defaults: any = {
			description: '',
			startDate: this.defaultStartDate,
			endDate: this.defaultStartDate && moment(this.defaultStartDate).add(1, 'h').toDate(),
			autoAssociateVod: !recordingDisabled,
			redirectVod: !recordingDisabled,
			emailToPreRegistrants: true
		};
		if(this.cloneWebcastId) {
			return this.EditWebcastService.cloneWebcast(this.cloneWebcastId, defaults);
		}
		return this.webcastId
			? this.EditWebcastService.getWebcast(this.webcastId)
			: this.EditWebcastService.initNewWebcastModel(defaults);

	}

	public submit($event): Promise<any> {
		return this.confirmAvailableHours()
			.then(() => this.trySave(), noop)
			.catch(err => {
				$event.failureCallback(err);
			});
	}

	public saveTemplate(): void {
		this.Dialog.getDialog(TemplateDialogName)
			.open({
				initialState: {
					templateInfo: {
						saveFunc: (name: string) => this.EditWebcastService.saveWebcastTemplate(name, this.webcast)
					}
				}
			});
	}

	private confirmAvailableHours(): Promise<any> {
		const namedOrActiveLicenseContentRestricted = this.accountLicense.isLicenseTypeNamedOrActive && this.mediaFeatures.recordingPolicy
			!== RecordingPolicy.DISABLE && (this.webcast.videoSource !== WebcastVideoSource.PRESENTATION || this.webcast.liveSubtitles?.isLiveSubtitlesEnabled);

		return (this.accountLicense.isLicenseTypeByHours || this.webcast.accessControl === EventAccessControl.Public || namedOrActiveLicenseContentRestricted) &&
			!this.accountLicense.viewingHoursValid
			? this.openViewingHoursAlertDialog()
			: Promise.resolve();
	}

	private openViewingHoursAlertDialog(): Promise<any> {
		return this.Dialog.openAlertDialog(
			this.Translate.instant('Warning'),
			this.Translate.instant('License_ViewingHours_NotAvailable'),
			this.Translate.instant('Ok')).result;

	}

	private trySave(): Promise<void> {
		const isEdit = !!this.webcast.id;

		this.status = { processing: true };

		this.webcast.cleanupData(this.mediaFeatures);

		return this.EditWebcastService.saveWebcast(this.webcast)
			.then(() => {
				this.status = { active: true };

				if(this.webcast.isCustomConsentEnabled && this.webcast.consentVerbiage !== this.webcast.customConsentDetails?.consentVerbiage
					&& this.webcast.customConsentDetails?.customConsentVerbiageUpdates)
				{
					this.addCustomConsentAuditLog();
				}

				(isEdit ? this.saveSuccessDialog : this.createSuccessDialog)
					.open().result
					.then(() => this.onSaved.emit({ webcast: this.webcast }));
			})
			.catch(err => {
				console.error('Save webcast err:', err);
				if (this.EditWebcastService.isFormErrorCode(err)) {
					this.status = { active: true };
					return Promise.reject(err);
				}

				this.status = { error: true };
			});
	}

	public selectTemplate(templateId: string): void {
		this.status = { processing: true };
		this.ready = false;
		this.WebcastTemplatesService.getTemplate(templateId)
			.then(template => {
				this.webcast.setAssignableCategories(this.assignableCategoryIds);
				return this.webcast.applyTemplate(template, this.webcast).then(() => {
					this.status = { active: true };
					this.ready = true;
				});
			})
			.catch(err => console.error(err));
	}

	private loadFlattenedCategories(): Promise<any> {
		return this.mediaFeatures.enableCategories &&
			this.CategoryService.getFlattenedCategories(this.UserContext.getAccount().id)
				.then(categories => this.categories = categories as any);
	}

	private addCustomConsentAuditLog(): void {
		const customConsentUpdate = {
			name: this.UserContext.getUser().fullName,
			consentVerbiage: this.webcast.consentVerbiage,
			submissionTimestamp: new Date(),
			username: this.UserContext.getUser().username
		};

		this.webcast.customConsentDetails.customConsentVerbiageUpdates.unshift(customConsentUpdate);
		this.webcast.customConsentDetails.consentVerbiage = this.webcast.consentVerbiage;
	}
}
